import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import DeliveryDiningOutlinedIcon from "@mui/icons-material/DeliveryDiningOutlined";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import { DataGrid } from "@mui/x-data-grid";
import {
  createUser,
  getDeliveryCompanies,
  getUsers,
  setDriverAvailability,
  setDriverCompany,
  setUserActiveStatus,
  setUserMaxStores,
  setUserPassword,
  updateUserProfile,
} from "../../api/adminService";
import { SetPasswordDialog } from "./AccountAccess";
import KeyIcon from "@mui/icons-material/KeyOutlined";
import { formatDate } from "./constants";

const CLIENT_TYPE = 1;
const BUSINESS_OWNER_TYPE = 2;
const DRIVER_TYPE = 3;
const ADMIN_TYPE = 4;
const DELIVERY_COMPANY_TYPE = 5;

const TYPE_META = {
  [CLIENT_TYPE]: { label: "Client", color: "info", icon: PeopleAltOutlinedIcon },
  [BUSINESS_OWNER_TYPE]: { label: "Business Owner", color: "success", icon: BusinessCenterOutlinedIcon },
  [DRIVER_TYPE]: { label: "Delivery Driver", color: "secondary", icon: DeliveryDiningOutlinedIcon },
  [ADMIN_TYPE]: { label: "Admin", color: "primary", icon: AdminPanelSettingsOutlinedIcon },
  [DELIVERY_COMPANY_TYPE]: { label: "Delivery Company", color: "warning", icon: DomainOutlinedIcon },
};

const USER_TYPE_TABS = [
  { value: CLIENT_TYPE, label: "Clients" },
  { value: BUSINESS_OWNER_TYPE, label: "Business Owners" },
  { value: DRIVER_TYPE, label: "Drivers" },
  { value: DELIVERY_COMPANY_TYPE, label: "Delivery Companies" },
  { value: ADMIN_TYPE, label: "Admins" },
];

function getInitials(name) {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

// wa.me needs the international number with digits only. Local numbers
// (leading 0) are assumed to be Israeli (+972), same as the support links.
function toWhatsAppUrl(phone) {
  let digits = (phone || "").replace(/\D/g, "");
  if (!digits) return null;
  if (digits.startsWith("00")) digits = digits.slice(2);
  else if (digits.startsWith("0")) digits = "972" + digits.slice(1);
  return `https://wa.me/${digits}`;
}

function UserAvatar({ fullName, userType, size = 38 }) {
  const meta = TYPE_META[userType] || TYPE_META[CLIENT_TYPE];
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        bgcolor: `${meta.color}.main`,
        fontSize: size * 0.38,
        fontWeight: 700,
      }}
    >
      {getInitials(fullName)}
    </Avatar>
  );
}

// Number field that saves on blur / Enter, so the admin can type a multi-digit limit without a request per keystroke.
function MaxStoresInput({ value, onSave, width = 80 }) {
  const [draft, setDraft] = useState(String(value ?? 1));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setDraft(String(value ?? 1));
  }, [value]);

  async function commit() {
    const next = Number(draft);
    if (!Number.isInteger(next) || next < 0 || next > 1000) {
      setError(true);
      return;
    }
    setError(false);
    if (next === (value ?? 1)) return;
    setSaving(true);
    try {
      await onSave(next);
    } catch {
      setError(true);
      setDraft(String(value ?? 1));
    } finally {
      setSaving(false);
    }
  }

  return (
    <TextField
      type="number"
      size="small"
      value={draft}
      error={error}
      disabled={saving}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") e.target.blur();
      }}
      slotProps={{ htmlInput: { min: 0, max: 1000, step: 1 } }}
      sx={{ width }}
    />
  );
}

export default function Users() {
  const [tab, setTab] = useState(CLIENT_TYPE);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{
          mb: 3,
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage clients, business owners, drivers, delivery companies and admins.
          </Typography>
        </Box>
        <Button
          variant="contained"
          disableElevation
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{ borderRadius: 2.5, px: 2.5, py: 1 }}
        >
          Add User
        </Button>
      </Stack>

      <Paper
        variant="outlined"
        sx={{ mb: 3, p: 1, borderRadius: 3, display: "inline-flex", maxWidth: "100%", overflow: "hidden" }}
      >
        <Tabs
          value={tab}
          onChange={(e, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          slotProps={{ indicator: { sx: { display: "none" } } }}
          sx={{ minHeight: 40 }}
        >
          {USER_TYPE_TABS.map((t) => {
            const Icon = TYPE_META[t.value].icon;
            return (
              <Tab
                key={t.value}
                value={t.value}
                icon={<Icon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={t.label}
                sx={{
                  minHeight: 40,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13.5,
                  color: "text.secondary",
                  px: 2,
                  mr: 0.5,
                  "&.Mui-selected": {
                    bgcolor: "primary.main",
                    color: "#fff",
                  },
                }}
              />
            );
          })}
        </Tabs>
      </Paper>

      <UserTable key={tab} userType={tab} refreshKey={refreshKey} />

      {createDialogOpen && (
        <CreateUserDialog
          onClose={() => setCreateDialogOpen(false)}
          onCreate={async (payload) => {
            await createUser(payload);
            setCreateDialogOpen(false);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </Box>
  );
}

function UserTable({ userType, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [deliveryCompanies, setDeliveryCompanies] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordUser, setPasswordUser] = useState(null);

  useEffect(() => {
    if (userType !== DRIVER_TYPE) return;
    getDeliveryCompanies()
      .then(setDeliveryCompanies)
      .catch(() => {});
  }, [userType]);

  function loadUsers() {
    setLoading(true);
    getUsers({
      type: userType,
      search: search || undefined,
      isActive: activeFilter === "" ? undefined : activeFilter === "active",
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    })
      .then((data) => {
        setRows(data.items);
        setRowCount(data.totalCount);
        setError("");
      })
      .catch(() => setError("Could not load users. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, paginationModel, refreshKey]);

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPaginationModel((p) => ({ ...p, page: 0 }));
    loadUsers();
  }

  async function handleToggleActive(row) {
    const next = !row.isActive;
    await setUserActiveStatus(row.userId, next);
    setRows((prev) => prev.map((r) => (r.userId === row.userId ? { ...r, isActive: next } : r)));
    if (selectedUser?.userId === row.userId) {
      setSelectedUser((prev) => ({ ...prev, isActive: next }));
    }
  }

  async function handleToggleAvailability(row) {
    const next = !row.isAvailable;
    await setDriverAvailability(row.userId, next);
    setRows((prev) => prev.map((r) => (r.userId === row.userId ? { ...r, isAvailable: next } : r)));
    if (selectedUser?.userId === row.userId) {
      setSelectedUser((prev) => ({ ...prev, isAvailable: next }));
    }
  }

  async function handleCompanyChange(row, companyUserId) {
    const data = await setDriverCompany(row.userId, companyUserId || null);
    setRows((prev) =>
      prev.map((r) =>
        r.userId === row.userId
          ? { ...r, deliveryCompanyUserId: data.deliveryCompanyUserId, deliveryCompanyName: data.deliveryCompanyName }
          : r
      )
    );
    if (selectedUser?.userId === row.userId) {
      setSelectedUser((prev) => ({
        ...prev,
        deliveryCompanyUserId: data.deliveryCompanyUserId,
        deliveryCompanyName: data.deliveryCompanyName,
      }));
    }
  }

  async function handleProfileSave(user, payload) {
    const data = await updateUserProfile(user.userId, payload);
    const patch = { email: data.email, fullName: data.fullName, phone: data.phone };
    setRows((prev) => prev.map((r) => (r.userId === user.userId ? { ...r, ...patch } : r)));
    if (selectedUser?.userId === user.userId) {
      setSelectedUser((prev) => ({ ...prev, ...patch }));
    }
  }

  async function handleMaxStoresChange(row, maxStores) {
    const data = await setUserMaxStores(row.userId, maxStores);
    setRows((prev) => prev.map((r) => (r.userId === row.userId ? { ...r, maxStores: data.maxStores } : r)));
    if (selectedUser?.userId === row.userId) {
      setSelectedUser((prev) => ({ ...prev, maxStores: data.maxStores }));
    }
  }

  const columns = useMemo(
    () => [
      {
        field: "fullName",
        headerName: "User",
        flex: 1.4,
        minWidth: 240,
        renderCell: (params) => (
          <Stack direction="row" spacing={1.5} sx={{ py: 1, alignItems: "center" }}>
            <UserAvatar fullName={params.row.fullName} userType={userType} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {params.row.fullName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                {params.row.email}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        field: "phone",
        headerName: "Phone",
        width: 140,
        renderCell: (params) => {
          const url = toWhatsAppUrl(params.row.phone);
          if (!url) return "-";
          return (
            <Box
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title="Open chat in WhatsApp"
              onClick={(e) => e.stopPropagation()}
              sx={{ color: "#128C7E", fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
            >
              {params.row.phone}
            </Box>
          );
        },
      },
      ...(userType === DRIVER_TYPE
        ? [
            {
              field: "extra",
              headerName: "Details",
              flex: 1,
              minWidth: 180,
              sortable: false,
              renderCell: (params) => (
                <Stack
                  direction="row"
                  spacing={0.75}
                  sx={{ flexWrap: "wrap", alignItems: "center" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Chip size="small" variant="outlined" label={params.row.vehicleType || "Driver"} />
                  <Chip
                    size="small"
                    color={params.row.isAvailable ? "success" : "default"}
                    label={params.row.isAvailable ? "Available" : "Not Available"}
                    onClick={() => handleToggleAvailability(params.row)}
                    sx={{ cursor: "pointer" }}
                  />
                </Stack>
              ),
            },
            {
              field: "deliveryCompanyUserId",
              headerName: "Delivery Company",
              flex: 1.2,
              minWidth: 220,
              sortable: false,
              renderCell: (params) => (
                <FormControl size="small" fullWidth onClick={(e) => e.stopPropagation()}>
                  <Select
                    value={params.row.deliveryCompanyUserId || ""}
                    displayEmpty
                    onChange={(e) => handleCompanyChange(params.row, e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Unlinked</em>
                    </MenuItem>
                    {deliveryCompanies.map((c) => (
                      <MenuItem key={c.userId} value={c.userId}>
                        {c.companyName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ),
            },
          ]
        : []),
      ...(userType === BUSINESS_OWNER_TYPE
        ? [
            {
              field: "extra",
              headerName: "Business",
              flex: 1,
              minWidth: 200,
              sortable: false,
              renderCell: (params) => (
                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                  <Typography variant="body2">{params.row.businessName || "-"}</Typography>
                  {params.row.isBusinessVerified && (
                    <VerifiedIcon sx={{ fontSize: 16, color: "info.main" }} />
                  )}
                </Stack>
              ),
            },
            {
              field: "maxStores",
              headerName: "Stores / Limit",
              width: 150,
              sortable: false,
              renderCell: (params) => (
                <Stack direction="row" spacing={1} sx={{ alignItems: "center", height: "100%" }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={(params.row.storeCount ?? 0) >= (params.row.maxStores ?? 1) ? "warning.main" : "text.primary"}
                  >
                    {params.row.storeCount ?? 0} /
                  </Typography>
                  <MaxStoresInput
                    value={params.row.maxStores ?? 1}
                    onSave={(next) => handleMaxStoresChange(params.row, next)}
                    width={72}
                  />
                </Stack>
              ),
            },
          ]
        : []),
      ...(userType === DELIVERY_COMPANY_TYPE
        ? [
            {
              field: "extra",
              headerName: "Company",
              flex: 1,
              minWidth: 200,
              sortable: false,
              renderCell: (params) => params.row.companyName || "-",
            },
          ]
        : []),
      {
        field: "createdAt",
        headerName: "Joined",
        width: 170,
        valueFormatter: (v) => formatDate(v),
      },
      {
        field: "lastEnteredAt",
        headerName: "Last Entered",
        width: 170,
        valueFormatter: (v) => (v ? formatDate(v) : "Never"),
      },
      {
        field: "isActive",
        headerName: "Status",
        width: 130,
        renderCell: (params) => (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={params.value}
              onChange={() => handleToggleActive(params.row)}
              size="small"
              color="success"
            />
            <Typography variant="caption" color={params.value ? "success.main" : "text.disabled"} fontWeight={600}>
              {params.value ? "Active" : "Off"}
            </Typography>
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userType, deliveryCompanies]
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        variant="outlined"
        component="form"
        onSubmit={handleSearchSubmit}
        sx={{ p: 2, mb: 2, borderRadius: 3 }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ alignItems: { xs: "stretch", sm: "center" } }}
        >
          <TextField
            placeholder="Search name, email or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 260, flexGrow: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Deactivated</MenuItem>
            </Select>
          </FormControl>
          <Button type="submit" variant="outlined" sx={{ borderRadius: 2.5 }}>
            Search
          </Button>
          <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {rowCount.toLocaleString()} {rowCount === 1 ? "user" : "users"}
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <DataGrid
          autoHeight
          rowHeight={64}
          rows={rows}
          getRowId={(row) => row.userId}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          onRowClick={(params) => setSelectedUser(params.row)}
        />
      </Paper>

      <Drawer
        anchor="right"
        open={Boolean(selectedUser)}
        onClose={() => setSelectedUser(null)}
        slotProps={{ paper: { sx: { width: { xs: "100%", sm: 380 } } } }}
      >
        {selectedUser && (
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                p: 3,
                pb: 2.5,
                background: "linear-gradient(135deg, #0B2B26 0%, #113B36 100%)",
                color: "#fff",
                position: "relative",
              }}
            >
              <IconButton
                onClick={() => setSelectedUser(null)}
                size="small"
                sx={{ position: "absolute", top: 12, right: 12, color: "rgba(255,255,255,0.7)" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                <UserAvatar fullName={selectedUser.fullName} userType={selectedUser.userType} size={52} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={700} noWrap>
                    {selectedUser.fullName}
                  </Typography>
                  <Chip
                    size="small"
                    label={TYPE_META[selectedUser.userType]?.label}
                    color={TYPE_META[selectedUser.userType]?.color}
                    sx={{ mt: 0.5, color: "#fff", fontWeight: 600 }}
                  />
                </Box>
              </Stack>
            </Box>

            <Box sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
              <Stack spacing={1.75}>
                <DetailRow label="Email" value={selectedUser.email} />
                <DetailRow label="Phone" value={selectedUser.phone || "-"} />
                <DetailRow label="Joined" value={formatDate(selectedUser.createdAt)} />
                <DetailRow
                  label="Last Entered"
                  value={selectedUser.lastEnteredAt ? formatDate(selectedUser.lastEnteredAt) : "Never"}
                />
                <DetailRow
                  label="Status"
                  value={
                    <Chip
                      size="small"
                      label={selectedUser.isActive ? "Active" : "Deactivated"}
                      color={selectedUser.isActive ? "success" : "default"}
                    />
                  }
                />
                {selectedUser.userType === BUSINESS_OWNER_TYPE && (
                  <>
                    <Divider />
                    <DetailRow label="Business Name" value={selectedUser.businessName || "-"} />
                    <DetailRow label="Stores Owned" value={selectedUser.storeCount ?? 0} />
                    <DetailRow
                      label="Max Stores Allowed"
                      value={
                        <MaxStoresInput
                          value={selectedUser.maxStores ?? 1}
                          onSave={(next) => handleMaxStoresChange(selectedUser, next)}
                        />
                      }
                    />
                    <DetailRow
                      label="Verified Business"
                      value={selectedUser.isBusinessVerified ? "Yes" : "No"}
                    />
                  </>
                )}
                {selectedUser.userType === DRIVER_TYPE && (
                  <>
                    <Divider />
                    <DetailRow label="Vehicle" value={selectedUser.vehicleType || "-"} />
                    <DetailRow
                      label="Available"
                      value={
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                          <Switch
                            checked={Boolean(selectedUser.isAvailable)}
                            onChange={() => handleToggleAvailability(selectedUser)}
                            size="small"
                            color="success"
                          />
                          <Typography
                            variant="caption"
                            color={selectedUser.isAvailable ? "success.main" : "text.disabled"}
                            fontWeight={600}
                          >
                            {selectedUser.isAvailable ? "Available" : "Not Available"}
                          </Typography>
                        </Stack>
                      }
                    />
                    <DetailRow label="Rating" value={selectedUser.driverRating ?? "-"} />
                    <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                      <InputLabel>Delivery Company</InputLabel>
                      <Select
                        label="Delivery Company"
                        value={selectedUser.deliveryCompanyUserId || ""}
                        onChange={(e) => handleCompanyChange(selectedUser, e.target.value)}
                      >
                        <MenuItem value="">
                          <em>Unlinked (independent)</em>
                        </MenuItem>
                        {deliveryCompanies.map((c) => (
                          <MenuItem key={c.userId} value={c.userId}>
                            {c.companyName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
                {selectedUser.userType === DELIVERY_COMPANY_TYPE && (
                  <>
                    <Divider />
                    <DetailRow label="Company Name" value={selectedUser.companyName || "-"} />
                  </>
                )}
              </Stack>
            </Box>

            <Stack spacing={1.25} sx={{ p: 2.5, borderTop: "1px solid", borderColor: "divider" }}>
              <Button
                fullWidth
                variant="contained"
                disableElevation
                startIcon={<EditIcon />}
                onClick={() => setEditingUser(selectedUser)}
                sx={{ borderRadius: 2.5 }}
              >
                Edit Name & Email
              </Button>
              {selectedUser.userType !== ADMIN_TYPE && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<KeyIcon />}
                  onClick={() => setPasswordUser(selectedUser)}
                  sx={{ borderRadius: 2.5 }}
                >
                  Change Password
                </Button>
              )}
              <Button
                fullWidth
                variant="outlined"
                color={selectedUser.isActive ? "error" : "success"}
                onClick={() => handleToggleActive(selectedUser)}
                sx={{ borderRadius: 2.5 }}
              >
                {selectedUser.isActive ? "Deactivate User" : "Activate User"}
              </Button>
            </Stack>
          </Box>
        )}
      </Drawer>

      {editingUser && (
        <EditUserDialog
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={async (payload) => {
            await handleProfileSave(editingUser, payload);
            setEditingUser(null);
          }}
        />
      )}

      {passwordUser && (
        <SetPasswordDialog
          user={passwordUser}
          onClose={() => setPasswordUser(null)}
          onSave={(password) => setUserPassword(passwordUser.userId, password)}
        />
      )}
    </Box>
  );
}

function EditUserDialog({ user, onClose, onSave }) {
  const [fullName, setFullName] = useState(user.fullName || "");
  const [email, setEmail] = useState(user.email || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const phoneTooLong = phone.trim().length > 20;
  const changed =
    fullName.trim() !== (user.fullName || "") ||
    email.trim() !== (user.email || "") ||
    phone.trim() !== (user.phone || "");
  const isValid = fullName.trim() && emailLooksValid && !phoneTooLong && changed;

  async function handleSubmit() {
    setSaving(true);
    setError("");
    try {
      await onSave({ fullName: fullName.trim(), email: email.trim(), phone: phone.trim() || null });
    } catch (err) {
      const data = err.response?.data;
      setError(typeof data === "string" ? data : "Could not update this user.");
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs" slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
        <Typography variant="h6" fontWeight={700}>
          Edit User
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Full Name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(email) && !emailLooksValid}
            helperText={email && !emailLooksValid ? "Enter a valid email" : "The user signs in with this email"}
            fullWidth
          />
          <TextField
            label="Phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={phoneTooLong}
            helperText={phoneTooLong ? "Phone number is too long (max 20 characters)" : "Leave empty to remove the phone number"}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!isValid || saving}
          onClick={handleSubmit}
          sx={{ borderRadius: 2.5, px: 3 }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CreateUserDialog({ onClose, onCreate }) {
  const [userType, setUserType] = useState(2);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [deliveryCompanyUserId, setDeliveryCompanyUserId] = useState("");
  const [deliveryCompanies, setDeliveryCompanies] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getDeliveryCompanies()
      .then(setDeliveryCompanies)
      .catch(() => {});
  }, []);

  const isValid = fullName.trim() && email.trim() && password.length >= 8;

  async function handleSubmit() {
    setSaving(true);
    setError("");
    try {
      await onCreate({
        userType,
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        password,
        businessName: userType === 2 ? businessName.trim() || undefined : undefined,
        vehicleType: userType === 3 ? vehicleType.trim() || undefined : undefined,
        companyName: userType === 5 ? companyName.trim() || undefined : undefined,
        deliveryCompanyUserId: userType === 3 ? deliveryCompanyUserId || undefined : undefined,
      });
    } catch (err) {
      setError(err.response?.data || "Could not create this user.");
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Add New User
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create a business owner, driver, delivery company or admin account.
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={2.5}>
          {error && (
            <Alert severity="error">{typeof error === "string" ? error : "Something went wrong."}</Alert>
          )}

          <FormControl size="small" fullWidth>
            <InputLabel>Account Type</InputLabel>
            <Select label="Account Type" value={userType} onChange={(e) => setUserType(e.target.value)}>
              <MenuItem value={2}>Business Owner</MenuItem>
              <MenuItem value={3}>Delivery Driver</MenuItem>
              <MenuItem value={5}>Delivery Company</MenuItem>
            </Select>
          </FormControl>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Full Name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                fullWidth
                autoFocus
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText="At least 8 characters"
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end">
                          {showPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
          </Grid>

          {userType === 2 && (
            <TextField
              label="Business Name"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              helperText="Defaults to full name if left blank"
              fullWidth
            />
          )}
          {userType === 3 && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Vehicle Type"
                  value={vehicleType}
                  onChange={(e) => setVehicleType(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Delivery Company</InputLabel>
                  <Select
                    label="Delivery Company"
                    value={deliveryCompanyUserId}
                    onChange={(e) => setDeliveryCompanyUserId(e.target.value)}
                  >
                    <MenuItem value="">Independent (no company)</MenuItem>
                    {deliveryCompanies.map((c) => (
                      <MenuItem key={c.userId} value={c.userId}>
                        {c.companyName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
          {userType === 5 && (
            <TextField
              label="Company Name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              helperText="Defaults to full name if left blank"
              fullWidth
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!isValid || saving}
          onClick={handleSubmit}
          sx={{ borderRadius: 2.5, px: 3 }}
        >
          Create User
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailRow({ label, value }) {
  return (
    <Stack direction="row" spacing={2} sx={{ justifyContent: "space-between", alignItems: "center" }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        component="div"
        variant="body2"
        fontWeight={600}
        sx={{ textAlign: "right", maxWidth: 200 }}
      >
        {value}
      </Typography>
    </Stack>
  );
}
